/**
 * Config helpers for plugins.
 *
 * These read environment variables and provide common URL helpers
 * without requiring plugins to import from agent-runner-server.
 */

/**
 * Returns the APP_URL from environment.
 * Defaults to 'http://localhost:4001' in development.
 */
export function getAppUrl(): string {
  return process.env.APP_URL ?? 'http://localhost:4001';
}

/**
 * Returns the API_URL from environment.
 * Falls back to localhost when unset.
 */
export function getApiUrl(): string {
  if (process.env.API_URL) return process.env.API_URL;
  const port = process.env.PORT ?? '4000';
  return `http://localhost:${port}/api`;
}

/**
 * THE upload directory — the single parse of `UPLOAD_DIR`, consumed by the
 * server (its config module re-exports this) and by plugins that mirror
 * files (e.g. plugin-slack). Centralized because a divergent reading here
 * once meant the server's upload janitor swept a different directory than
 * plugins wrote to — silently unbounded growth for anyone setting
 * `UPLOAD_DIR`.
 */
export function getUploadDir(): string {
  return process.env.UPLOAD_DIR ?? './data/uploads';
}

/**
 * Whether the pod-local upload dir may be written to and read from — the
 * single runtime interpretation of `UPLOAD_LOCAL_STORAGE` (disabled by
 * default in every environment; enabling it is an explicit local-dev
 * opt-in). The server re-exports this alongside its boot-time Zod
 * validation of the raw value, so host and plugins can never disagree on
 * what "enabled" means.
 */
export function isLocalUploadStorageEnabled(): boolean {
  return process.env.UPLOAD_LOCAL_STORAGE === 'enabled';
}

/**
 * Type for simple env config access.
 */
export interface EnvConfig {
  APP_URL: string;
  API_URL?: string;
  PORT: number;
  NODE_ENV: 'development' | 'production' | 'test';
  RAILWAY_ENVIRONMENT_NAME?: string;
}

/**
 * Returns a simple env config object with common values.
 * For plugins that need basic config access without the full server schema.
 */
export function getEnvConfig(): EnvConfig {
  return {
    APP_URL: process.env.APP_URL ?? 'http://localhost:4001',
    API_URL: process.env.API_URL,
    PORT: parseInt(process.env.PORT ?? '4000', 10),
    NODE_ENV: (process.env.NODE_ENV as EnvConfig['NODE_ENV']) ?? 'development',
    RAILWAY_ENVIRONMENT_NAME: process.env.RAILWAY_ENVIRONMENT_NAME,
  };
}

/**
 * The set of GitHub endpoints a deployment talks to. When
 * `GITHUB_ENTERPRISE_URL` is unset (the default) these are the github.com /
 * api.github.com endpoints, unchanged from before. When an operator points the
 * deployment at a GitHub Enterprise Server instance, every endpoint is
 * re-derived from that host so OAuth sign-in, the GitHub App, and the agent
 * devbox's git/gh setup all target the enterprise instance.
 */
export interface GitHubEndpoints {
  /** Bare host (with port if any), e.g. `github.com` or `github.acme.com`. */
  host: string;
  /** Web origin, e.g. `https://github.com` or `https://github.acme.com`. */
  webBaseUrl: string;
  /** REST API base, e.g. `https://api.github.com` or `https://github.acme.com/api/v3`. */
  apiBaseUrl: string;
  /** OAuth authorize endpoint, e.g. `https://github.com/login/oauth/authorize`. */
  oauthAuthorizeUrl: string;
  /** OAuth access-token endpoint, e.g. `https://github.com/login/oauth/access_token`. */
  oauthTokenUrl: string;
  /** True only when a non-github.com (GitHub Enterprise Server) host is configured. */
  isEnterprise: boolean;
  /**
   * Host of this deployment's remote GitHub MCP server, or `undefined` when
   * this GitHub deployment publishes no remote MCP server at all. GitHub
   * Enterprise Cloud with data residency (a `*.ghe.com` subdomain) serves one
   * at `copilot-api.<subdomain>.ghe.com`, the same shape as `api.githubcopilot.com`
   * for github.com cloud. A self-hosted GitHub Enterprise Server instance
   * publishes no remote MCP server — only a local stdio binary, a different
   * transport entirely — so there is no host to derive.
   */
  mcpHost: string | undefined;
}

const GITHUB_CLOUD_ENDPOINTS: GitHubEndpoints = {
  host: 'github.com',
  webBaseUrl: 'https://github.com',
  apiBaseUrl: 'https://api.github.com',
  oauthAuthorizeUrl: 'https://github.com/login/oauth/authorize',
  oauthTokenUrl: 'https://github.com/login/oauth/access_token',
  isEnterprise: false,
  mcpHost: 'api.githubcopilot.com',
};

/** Hosts that are really github.com cloud, however the operator spells the URL. */
const GITHUB_CLOUD_HOSTS = new Set(['github.com', 'www.github.com', 'api.github.com']);

/** Suffix of a GitHub Enterprise Cloud data-residency host, e.g. `octocorp.ghe.com`. */
const GHE_DATA_RESIDENCY_SUFFIX = '.ghe.com';

/**
 * Derive the GitHub endpoints for a deployment from an optional GitHub
 * Enterprise Server base URL.
 *
 * - empty / undefined → github.com cloud (today's behavior).
 * - a github.com URL → also cloud, so the REST API stays `api.github.com`
 *   rather than the nonsensical `github.com/api/v3`.
 * - anything else → GitHub Enterprise Server: the REST API lives under
 *   `/api/v3` on the same host and OAuth under `/login/oauth/*`. A bare host
 *   (`github.acme.com`) is accepted and assumed to be https.
 *
 * Pure (no env access) so it is trivially unit-testable; {@link getGitHubEndpoints}
 * is the only reader of `process.env`. Malformed input fails safe to cloud
 * rather than throwing during provisioning.
 */
export function resolveGitHubEndpoints(enterpriseUrl: string | null | undefined): GitHubEndpoints {
  const raw = enterpriseUrl?.trim();
  if (!raw) return GITHUB_CLOUD_ENDPOINTS;

  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return GITHUB_CLOUD_ENDPOINTS;
  }

  if (GITHUB_CLOUD_HOSTS.has(url.host.toLowerCase())) return GITHUB_CLOUD_ENDPOINTS;

  const webBaseUrl = `${url.protocol}//${url.host}`;
  const hostLower = url.host.toLowerCase();
  return {
    host: url.host,
    webBaseUrl,
    apiBaseUrl: `${webBaseUrl}/api/v3`,
    oauthAuthorizeUrl: `${webBaseUrl}/login/oauth/authorize`,
    oauthTokenUrl: `${webBaseUrl}/login/oauth/access_token`,
    isEnterprise: true,
    mcpHost: hostLower.endsWith(GHE_DATA_RESIDENCY_SUFFIX) ? `copilot-api.${url.host}` : undefined,
  };
}

/**
 * The deployment's GitHub endpoints, derived from `GITHUB_ENTERPRISE_URL`.
 * Operator-wide and read at call time so a value set via env / Railway / a
 * local `.env` is honored without a code change. Defaults to github.com.
 */
export function getGitHubEndpoints(): GitHubEndpoints {
  // `parseGitHubRepo` / `normalizeRepoUrl` / `toGitHubRepoUrl` are shared with
  // the browser bundle (the GitHub plugin's RepoSelector, the blueprint create
  // page, ...), where `process` is undefined. Guard the env read so those paths
  // fall back to github.com cloud instead of throwing `process is not defined`.
  // `GITHUB_ENTERPRISE_URL` is a server-only var, so the browser never resolved
  // it anyway — this matches the pre-enterprise github.com-only browser behavior.
  const enterpriseUrl =
    typeof process !== 'undefined' ? process.env?.GITHUB_ENTERPRISE_URL : undefined;
  return resolveGitHubEndpoints(enterpriseUrl);
}

/**
 * The set of GitLab endpoints a deployment talks to. When `GITLAB_BASE_URL`
 * is unset (the default) these are the gitlab.com endpoints. When an operator
 * points the deployment at a self-managed GitLab instance, every endpoint is
 * re-derived from that base so OAuth sign-in and API calls all target the
 * self-managed instance.
 */
export interface GitLabEndpoints {
  /** Web origin, e.g. `https://gitlab.com` or `https://gitlab.acme.com`. */
  baseUrl: string;
  /** REST API base, e.g. `https://gitlab.com/api/v4`. */
  apiBaseUrl: string;
  /** OAuth authorize endpoint, e.g. `https://gitlab.com/oauth/authorize`. */
  oauthAuthorizeUrl: string;
  /** OAuth token endpoint, e.g. `https://gitlab.com/oauth/token`. */
  oauthTokenUrl: string;
}

/**
 * The deployment's GitLab endpoints, derived from `GITLAB_BASE_URL`.
 * Operator-wide and read at call time so a value set via env / Railway / a
 * local `.env` is honored without a code change. Defaults to gitlab.com.
 * A trailing slash on the configured base URL is ignored.
 */
export function getGitLabEndpoints(): GitLabEndpoints {
  // Guard the env read so browser-shared code paths fall back to gitlab.com
  // instead of throwing `process is not defined` (mirrors
  // {@link getGitHubEndpoints}).
  const raw = typeof process !== 'undefined' ? process.env?.GITLAB_BASE_URL : undefined;
  const trimmed = raw?.trim().replace(/\/+$/, '');
  const baseUrl = trimmed ? trimmed : 'https://gitlab.com';
  return {
    baseUrl,
    apiBaseUrl: `${baseUrl}/api/v4`,
    oauthAuthorizeUrl: `${baseUrl}/oauth/authorize`,
    oauthTokenUrl: `${baseUrl}/oauth/token`,
  };
}

/**
 * The web URL of a registered GitHub App's public page for the configured host.
 * github.com serves this under `/apps/<slug>`; GitHub Enterprise Server serves
 * it under `/github-apps/<slug>`. Appending `/installations/new` yields the
 * install deeplink. Used as the fallback when GitHub's own `html_url` (which
 * already encodes the correct path for its host) is unavailable.
 */
export function githubAppPageUrl(slug: string): string {
  const { webBaseUrl, isEnterprise } = getGitHubEndpoints();
  return `${webBaseUrl}/${isEnterprise ? 'github-apps' : 'apps'}/${slug}`;
}

/**
 * Egress allow-list hostnames for the deployment's GitHub host, for use in
 * devbox network policies. Takes the web origin (`PublicConfig.githubWebBaseUrl`
 * / `useGitHubWebBaseUrl()`) so it works in the browser, where env-derived
 * `getGitHubEndpoints()` always resolves to cloud.
 *
 * On github.com cloud the surface spans three domains:
 * - `github.com` — git-over-HTTPS and the web UI
 * - `*.github.com` — `api.github.com` (gh CLI / REST) and `codeload.github.com`
 *   (tarball downloads)
 * - `*.githubusercontent.com` — raw file fetches plus release-asset and
 *   git-lfs downloads, which redirect to `objects.githubusercontent.com`
 *
 * A GitHub Enterprise Server instance serves all of that from its single host
 * (API under `/api/v3`), so the list collapses to that host. Malformed input
 * fails safe to the cloud list, mirroring {@link resolveGitHubEndpoints}.
 */
export function githubEgressHostnames(webBaseUrl: string): string[] {
  const cloud = ['github.com', '*.github.com', '*.githubusercontent.com'];
  let host: string;
  try {
    host = new URL(webBaseUrl).host;
  } catch {
    return cloud;
  }
  if (GITHUB_CLOUD_HOSTS.has(host.toLowerCase())) return cloud;
  return [host];
}
